import { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box, Typography, Button, Select, MenuItem, TextField, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, Paper, Chip, CircularProgress,
  FormControl, InputLabel, Grid, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, useMediaQuery, useTheme, Tooltip, List, ListItem, ListItemText,
} from "@mui/material";
import {
  Download, Clear, Visibility, Close, Delete, Add, Edit, Save,
  CalendarMonth, EventAvailable, DeleteSweep, ChevronLeft, ChevronRight,
} from "@mui/icons-material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  GetAttendanceHistory,
  GetHolidays,
  AddHoliday,
  DeleteHoliday,
  DeleteMonthlyRecords,
  EditAttendanceRecord,
} from "../../../Services/Services";

function EmployeeAttendance() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [records, setRecords] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [filter, setFilter] = useState("month");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [showDateRange, setShowDateRange] = useState(false);
  const [loading, setLoading] = useState(false);

  const [sortBy, setSortBy] = useState("name_asc");
  const [monthOffset, setMonthOffset] = useState(0);

  // Modals
  const [viewOpen, setViewOpen] = useState(false);
  const [viewEmployee, setViewEmployee] = useState(null);
  const [holidayOpen, setHolidayOpen] = useState(false);
  const [holidayDate, setHolidayDate] = useState("");
  const [holidayName, setHolidayName] = useState("");
  const [deleteMonthOpen, setDeleteMonthOpen] = useState(false);
  const [deleteMonth, setDeleteMonth] = useState("");

  // Edit record
  const [editRecord, setEditRecord] = useState(null);
  const [editInTime, setEditInTime] = useState("");
  const [editOutTime, setEditOutTime] = useState("");
  const [editStatus, setEditStatus] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const compactInput = {
    "& .MuiInputBase-root": { height: 32, borderRadius: 0 },
    "& .MuiInputLabel-root": { fontSize: "0.7rem" },
  };
  const btnStyle = { height: 32, fontSize: "0.7rem", borderRadius: 0 };

  const todayStr = useMemo(() => {
    const today = new Date();
    const y = today.getFullYear();
    const m = String(today.getMonth() + 1).padStart(2, "0");
    const d = String(today.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }, []);

  const currentMonthName = useMemo(() => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() + monthOffset);
    return d.toLocaleString("default", { month: "long", year: "numeric" });
  }, [monthOffset]);

  const loadAttendance = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter) params.append("filter", filter);
      if (search) params.append("search", search);
      if (sortBy) params.append("sort_by", sortBy);
      if (filter === "month") params.append("month_offset", monthOffset);
      if (filter === "custom") {
        if (fromDate) params.append("from", fromDate);
        if (toDate) params.append("to", toDate);
      }
      const responseData = await GetAttendanceHistory(params.toString());
      if (responseData?.records) {
        setRecords(responseData.records);
        setHolidays(responseData.holidays || []);
      } else {
        setRecords(Array.isArray(responseData) ? responseData : []);
      }
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.message || "Failed to load attendance");
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [filter, search, fromDate, toDate, monthOffset, sortBy]);

  const loadHolidays = async () => {
    try {
      const data = await GetHolidays();
      setHolidays(Array.isArray(data) ? data : []);
    } catch (err) {
      console.log(err);
      toast.error("Failed to load holidays");
    }
  };

  useEffect(() => { loadAttendance(); }, [loadAttendance]);
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 500);
    return () => clearTimeout(t);
  }, [searchInput]);

  const holidayMap = useMemo(() => {
    const map = {};
    holidays.forEach(h => { map[h.date] = h.name; });
    return map;
  }, [holidays]);

  const { employeeMap, dates } = useMemo(() => {
    const empMap = {};
    const dateSet = new Set();
    const orderedKeys = [];

    records.forEach(r => {
      const key = r.unique_id;
      if (!empMap[key]) {
        empMap[key] = {
          unique_id: r.unique_id, name: r.name, mobile: r.mobile,
          email: r.email, address: r.address,
          created_at: r.created_at,
          attendance: {},
        };
        orderedKeys.push(key);
      }
      empMap[key].attendance[r.date] = r;
      dateSet.add(r.date);
    });

    holidays.forEach(h => dateSet.add(h.date));

    if (filter === "month") {
      const d = new Date();
      d.setDate(1);
      d.setMonth(d.getMonth() + monthOffset);
      const year = d.getFullYear();
      const month = d.getMonth();
      const daysInMonth = new Date(year, month + 1, 0).getDate();

      for (let day = 1; day <= daysInMonth; day++) {
        const dateStr = `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
        dateSet.add(dateStr);
      }
    }

    const employees = orderedKeys.map(key => empMap[key]);

    return {
      employeeMap: employees,
      dates: Array.from(dateSet).sort(),
    };
  }, [records, holidays, filter, monthOffset]);

  const handleFilterChange = (e) => {
    const v = e.target.value;
    setFilter(v);
    setShowDateRange(v === "custom");
    setMonthOffset(0);
    if (v !== "custom") { setFromDate(""); setToDate(""); }
  };

  const clearFilters = () => {
    setFilter("month"); setSearch(""); setSearchInput("");
    setFromDate(""); setToDate(""); setShowDateRange(false);
    setMonthOffset(0); setSortBy("name_asc");
  };

  const openView = (emp) => {
    setViewEmployee(emp);
    setViewOpen(true);
  };

  const openHolidayModal = () => {
    setHolidayDate(""); setHolidayName("");
    setHolidayOpen(true);
    loadHolidays();
  };

  const addHoliday = async () => {
    if (!holidayDate || !holidayName.trim()) {
      toast.error("Date and name required");
      return;
    }
    try {
      await AddHoliday({ date: holidayDate, name: holidayName.trim() });
      toast.success("Holiday added successfully");
      setHolidayDate("");
      setHolidayName("");
      loadHolidays();
      loadAttendance();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to add holiday");
    }
  };

  const deleteHoliday = async (id) => {
    if (!window.confirm("Delete this holiday?")) return;
    try {
      await DeleteHoliday(id);
      toast.success("Holiday deleted");
      loadHolidays();
      loadAttendance();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete holiday");
    }
  };

  const handleMonthlyDelete = async () => {
    if (!deleteMonth) return toast.error("Select month");
    if (!window.confirm(`Delete ALL records for ${deleteMonth}?`)) return;
    try {
      const [year, month] = deleteMonth.split("-");
      const recordsToDelete = records.filter(r => {
        const d = new Date(r.date);
        return d.getFullYear() === parseInt(year) && (d.getMonth() + 1) === parseInt(month);
      }).map(r => ({ unique_id: r.unique_id, date: r.date }));

      if (recordsToDelete.length === 0) {
        toast.info("No records for this month");
        return;
      }
      await DeleteMonthlyRecords({ records: recordsToDelete });
      toast.success(`${recordsToDelete.length} records deleted`);
      setDeleteMonthOpen(false);
      setDeleteMonth("");
      loadAttendance();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete records");
    }
  };

  const openEditRecord = (date, record) => {
    setEditRecord({ date, ...record });
    setEditInTime(record?.in_time && record.in_time !== "--" ? convertTo24(record.in_time) : "");
    setEditOutTime(record?.out_time && record.out_time !== "--" ? convertTo24(record.out_time) : "");
    setEditStatus(record?.status || "Present");
  };

  const convertTo24 = (time12) => {
    if (!time12 || time12 === "--") return "";
    const [time, meridiem] = time12.split(" ");
    let [h, m] = time.split(":");
    h = parseInt(h);
    if (meridiem === "PM" && h !== 12) h += 12;
    if (meridiem === "AM" && h === 12) h = 0;
    return `${String(h).padStart(2, "0")}:${m}`;
  };

  const saveEditRecord = async () => {
    if (!editRecord) return;
    setSavingEdit(true);
    try {
      await EditAttendanceRecord({
        unique_id: viewEmployee.unique_id,
        date: editRecord.date,
        in_time: editInTime,
        out_time: editOutTime,
        status: editStatus,
      });
      toast.success("Record updated successfully");
      setEditRecord(null);
      loadAttendance();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update record");
    } finally {
      setSavingEdit(false);
    }
  };

  const downloadEmployeeCSV = (emp) => {
    const dateList = Object.keys(emp.attendance).sort();
    const headers = ["S.No.", "Date", "Status", "In Time", "Out Time", "Total", "Type"];
    const rows = dateList.map((date, i) => {
      const r = emp.attendance[date];
      return [
        i + 1, date,
        holidayMap[date] ? `Holiday (${holidayMap[date]})` : r.status,
        r.in_time || "-", r.out_time || "-",
        r.total_time || "-", r.auto_marked ? "Auto" : "Manual",
      ];
    });
    const csv = [
      `Employee: ${emp.name} (${emp.unique_id})`, "",
      headers.join(","), ...rows.map(r => r.join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `${emp.unique_id}_${emp.name.replace(/\s/g, "_")}.csv`;
    link.click();
    toast.success("Download started");
  };

  const downloadFullCSV = () => {
    if (!records.length) {
      toast.warning("No records to download");
      return;
    }
    const headers = ["S.No.", "Date", "Emp ID", "Name", "Status", "In", "Out", "Total", "Type"];
    const rows = records.map((it, i) => [
      i + 1, it.date, it.unique_id, `"${(it.name || "").replace(/"/g, '""')}"`,
      holidayMap[it.date] ? `Holiday (${holidayMap[it.date]})` : it.status,
      it.in_time || "", it.out_time || "", it.total_time || "",
      it.auto_marked ? "Auto" : "Manual",
    ]);
    const csv = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `Attendance_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    toast.success("Download started");
  };

  const getCellStyle = (record, date) => {
    if (date > todayStr) {
      return { bg: "#f3f4f6", color: "#9ca3af", letter: "", label: "Future Date", isFuture: true };
    }
    if (holidayMap[date]) return { bg: "#fef9c3", color: "#a16207", letter: "H", label: holidayMap[date], isFuture: false };
    if (!record) return { bg: "#f9fafb", color: "#d1d5db", letter: "-", label: "No record", isFuture: false };
    if (record.status === "Present") return { bg: "#dcfce7", color: "#16a34a", letter: "P", label: "Present", isFuture: false };
    return { bg: "#fee2e2", color: "#dc2626", letter: "A", label: "Absent", isFuture: false };
  };

  return (
    <Box sx={{ px: 0, pt: 0, mt: -3, maxWidth: 1400, mx: "auto" }}>
      <ToastContainer />

      {/* FILTERS ROW 1 */}
      <Grid container spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <Grid item xs={12} sm={1.8}>
          <FormControl fullWidth size="small" sx={compactInput}>
            <InputLabel>Filter</InputLabel>
            <Select value={filter} label="Filter" onChange={handleFilterChange}>
              <MenuItem value="today">Today</MenuItem>
              <MenuItem value="week">This Week</MenuItem>
              <MenuItem value="month">This Month</MenuItem>
              <MenuItem value="year">This Year</MenuItem>
              <MenuItem value="all">All Records</MenuItem>
              <MenuItem value="custom">Custom Range</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={1.8}>
          <FormControl fullWidth size="small" sx={compactInput}>
            <InputLabel>Sort By</InputLabel>
            <Select value={sortBy} label="Sort By" onChange={(e) => setSortBy(e.target.value)}>
              <MenuItem value="name_asc">Name (A-Z)</MenuItem>
              <MenuItem value="name_desc">Name (Z-A)</MenuItem>
              <MenuItem value="new_old">Newest First</MenuItem>
              <MenuItem value="old_new">Oldest First</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={2.2}>
          <TextField fullWidth size="small" label="Search name..."
            value={searchInput} onChange={(e) => setSearchInput(e.target.value)}
            sx={compactInput} />
        </Grid>

        {showDateRange && (
          <>
            <Grid item xs={6} sm={1.4}>
              <TextField fullWidth size="small" type="date" label="From"
                value={fromDate} onChange={(e) => setFromDate(e.target.value)}
                InputLabelProps={{ shrink: true }} sx={compactInput} />
            </Grid>
            <Grid item xs={6} sm={1.4}>
              <TextField fullWidth size="small" type="date" label="To"
                value={toDate} onChange={(e) => setToDate(e.target.value)}
                InputLabelProps={{ shrink: true }} sx={compactInput} />
            </Grid>
          </>
        )}

        <Grid item xs={4} sm={0.9}>
          <Button fullWidth variant="contained" color="error" size="small"
            onClick={clearFilters} startIcon={<Clear />} sx={btnStyle}>
            Clear
          </Button>
        </Grid>
        <Grid item xs={4} sm={1.3}>
          <Button fullWidth variant="contained" color="warning" size="small"
            onClick={openHolidayModal} startIcon={<CalendarMonth />} sx={btnStyle}>
            Holidays
          </Button>
        </Grid>
        <Grid item xs={4} sm={1.3}>
          <Button fullWidth variant="contained" color="error" size="small"
            onClick={() => setDeleteMonthOpen(true)} startIcon={<DeleteSweep />} sx={btnStyle}>
            Del Month
          </Button>
        </Grid>
        <Grid item xs={12} sm={1.3}>
          <Button fullWidth variant="contained" size="small"
            onClick={downloadFullCSV} startIcon={<Download />} sx={btnStyle}>
            CSV
          </Button>
        </Grid>
      </Grid>

      {filter === "month" && (
        <Box sx={{ mb: 1, display: "flex", gap: 1, alignItems: "center", justifyContent: "center", p: 1, bgcolor: "#e0f2fe", borderRadius: 1 }}>
          <IconButton size="small" onClick={() => setMonthOffset(prev => prev - 1)}
            sx={{ bgcolor: "#fff", "&:hover": { bgcolor: "#bae6fd" } }}>
            <ChevronLeft />
          </IconButton>
          <Typography sx={{ fontSize: "0.9rem", fontWeight: 700, minWidth: 180, textAlign: "center", color: "#0369a1" }}>
            📅 {currentMonthName}
          </Typography>
          <IconButton size="small" onClick={() => setMonthOffset(prev => prev + 1)}
            sx={{ bgcolor: "#fff", "&:hover": { bgcolor: "#bae6fd" } }}>
            <ChevronRight />
          </IconButton>
          {monthOffset !== 0 && (
            <Button size="small" variant="outlined" onClick={() => setMonthOffset(0)}
              sx={{ ml: 1, fontSize: "0.65rem", height: 28 }}>
              Back to Current
            </Button>
          )}
        </Box>
      )}

      <Box sx={{ mb: 1, p: 1, bgcolor: "#f1f5f9", borderRadius: 1, display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
          <Typography sx={{ fontSize: "0.75rem" }}>
            <strong>{employeeMap.length}</strong> employees • <strong>{dates.length}</strong> days
          </Typography>
          <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
            <Box sx={{ width: 14, height: 14, bgcolor: "#dcfce7", borderRadius: "2px", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "0.55rem", fontWeight: 700, color: "#16a34a" }}>P</Box>
            <Typography sx={{ fontSize: "0.7rem" }}>Present</Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
            <Box sx={{ width: 14, height: 14, bgcolor: "#fee2e2", borderRadius: "2px", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "0.55rem", fontWeight: 700, color: "#dc2626" }}>A</Box>
            <Typography sx={{ fontSize: "0.7rem" }}>Absent</Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
            <Box sx={{ width: 14, height: 14, bgcolor: "#fef9c3", borderRadius: "2px", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "0.55rem", fontWeight: 700, color: "#a16207" }}>H</Box>
            <Typography sx={{ fontSize: "0.7rem" }}>Holiday</Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
            <Box sx={{ width: 14, height: 14, bgcolor: "#f3f4f6", border: "1px dashed #d1d5db", borderRadius: "2px" }}></Box>
            <Typography sx={{ fontSize: "0.7rem", color: "#6b7280" }}>Future</Typography>
          </Box>
        </Box>
        <Typography sx={{ fontSize: "0.8rem", fontWeight: "bold", color: "#0f172a" }}>
          {filter === "month" ? currentMonthName : "Custom View"}
        </Typography>
      </Box>

      {loading && <Box sx={{ textAlign: "center", py: 3 }}><CircularProgress size={24} /></Box>}

      {!loading && (
        <TableContainer component={Paper} sx={{ maxHeight: "85vh", overflow: "auto" }}>
          <Table stickyHeader size="small" sx={{
            "& .MuiTableCell-root": {
              padding: "2px 3px !important",
              whiteSpace: "nowrap",
              borderBottom: "1px solid #e5e7eb",
              borderRight: "1px solid #f1f5f9",
              fontSize: "0.65rem",
            },
            "& thead th": { fontWeight: "bold", bgcolor: "#f8fafc" },
          }}>
            <TableHead>
              <TableRow>
                <TableCell colSpan={dates.length + 4} sx={{ bgcolor: "#f1f5f9", py: "4px !important", px: 2 }}>
                  <Typography sx={{ fontSize: "0.7rem", fontWeight: 700, color: "#334155" }}>
                    Attendance Sheet: {filter === "month" ? currentMonthName : "Filtered View"} 
                    {" • "}Total Employees: {employeeMap.length}
                  </Typography>
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell align="center" sx={{
                  position: "sticky", left: 0, bgcolor: "#f8fafc", zIndex: 11,
                  minWidth: 28, maxWidth: 28,
                }}>
                  #
                </TableCell>
                <TableCell align="left" sx={{
                  position: "sticky", left: 28, bgcolor: "#f8fafc", zIndex: 11,
                  minWidth: 100, maxWidth: 100,
                }}>
                  Employee
                </TableCell>

                {dates.map((d) => {
                  const isHoliday = holidayMap[d];
                  const dateNum = new Date(d).getDate();
                  const isFuture = d > todayStr;
                  return (
                    <TableCell key={d} align="center" sx={{
                      minWidth: 22, maxWidth: 22,
                      bgcolor: isFuture ? "#f3f4f6 !important" : (isHoliday ? "#fef9c3 !important" : "#f8fafc"),
                      color: isFuture ? "#9ca3af" : (isHoliday ? "#a16207" : "#334155"),
                      padding: "3px 1px !important",
                      fontSize: "0.65rem !important",
                      fontWeight: 700,
                      opacity: isFuture ? 0.6 : 1
                    }}>
                      <Tooltip title={isFuture ? `Future: ${d}` : (isHoliday ? `🎉 ${isHoliday} - ${d}` : d)} arrow>
                        <Box sx={{ fontSize: "0.65rem", lineHeight: 1 }}>{dateNum}</Box>
                      </Tooltip>
                    </TableCell>
                  );
                })}

                <TableCell align="center" sx={{
                  position: "sticky", right: 45, bgcolor: "#f8fafc", zIndex: 11,
                  minWidth: 55, maxWidth: 55,
                }}>
                  P/A
                </TableCell>

                <TableCell align="center" sx={{
                  position: "sticky", right: 0, bgcolor: "#f8fafc", zIndex: 11,
                  minWidth: 45, maxWidth: 45,
                  boxShadow: "-2px 0 5px -2px rgba(0,0,0,0.15)",
                }}>
                  View
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {employeeMap.length > 0 ? employeeMap.map((emp, i) => {
                const presentCount = Object.values(emp.attendance).filter(r => r.status === "Present" && r.date <= todayStr).length;
                const absentCount = Object.values(emp.attendance).filter(r => r.status === "Absent" && r.date <= todayStr).length;
                const bgColor = i % 2 === 0 ? "#fafafa" : "#fff";

                return (
                  <TableRow key={emp.unique_id} hover
                    sx={{ bgcolor: bgColor, "&:hover": { bgcolor: "#e0f2fe !important" } }}>
                    <TableCell align="center" sx={{
                      position: "sticky", left: 0, bgcolor: bgColor, zIndex: 5,
                      minWidth: 28, maxWidth: 28,
                      fontSize: "0.65rem",
                    }}>
                      {i + 1}
                    </TableCell>
                    <TableCell align="left" sx={{
                      position: "sticky", left: 28, bgcolor: bgColor, zIndex: 5,
                      minWidth: 100, maxWidth: 100,
                      overflow: "hidden", textOverflow: "ellipsis",
                    }}>
                      <Tooltip title={`${emp.name} (${emp.unique_id})`} arrow>
                        <Typography sx={{
                          fontSize: "0.7rem", fontWeight: 600,
                          overflow: "hidden", textOverflow: "ellipsis",
                          lineHeight: 1.2,
                        }}>
                          {emp.name}
                        </Typography>
                      </Tooltip>
                    </TableCell>

                    {dates.map((d) => {
                      const record = emp.attendance[d];
                      const style = getCellStyle(record, d);
                      return (
                        <TableCell key={d} align="center" sx={{
                          padding: "0 !important",
                          minWidth: 22, maxWidth: 22,
                          bgcolor: style.bg,
                          border: style.isFuture ? "1px dashed #e5e7eb" : undefined,
                        }}>
                          <Tooltip title={`${style.label} - ${d}`} arrow>
                            <Box sx={{
                              width: "100%", height: 22,
                              display: "flex", alignItems: "center", justifyContent: "center",
                              color: style.color,
                              fontSize: "0.7rem",
                              fontWeight: 700,
                            }}>
                              {style.letter}
                            </Box>
                          </Tooltip>
                        </TableCell>
                      );
                    })}

                    <TableCell align="center" sx={{
                      position: "sticky", right: 45, bgcolor: bgColor, zIndex: 5,
                      minWidth: 55, maxWidth: 55,
                      padding: "2px !important",
                    }}>
                      <Box sx={{ display: "flex", gap: 0.2, justifyContent: "center" }}>
                        <Box sx={{
                          bgcolor: "#dcfce7", color: "#16a34a",
                          px: 0.4, borderRadius: "2px",
                          fontSize: "0.6rem", fontWeight: 700, minWidth: 20,
                        }}>
                          {presentCount}
                        </Box>
                        <Box sx={{
                          bgcolor: "#fee2e2", color: "#dc2626",
                          px: 0.4, borderRadius: "2px",
                          fontSize: "0.6rem", fontWeight: 700, minWidth: 20,
                        }}>
                          {absentCount}
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell align="center" sx={{
                      position: "sticky", right: 0, bgcolor: bgColor, zIndex: 5,
                      minWidth: 45, maxWidth: 45,
                      padding: "0 !important",
                    }}>
                      <IconButton size="small" onClick={() => openView(emp)}
                        sx={{
                          color: "#0284c7",
                          padding: "2px",
                          "&:hover": { bgcolor: "#e0f2fe" },
                        }}>
                        <Visibility sx={{ fontSize: 14 }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              }) : (
                <TableRow>
                  <TableCell colSpan={dates.length + 4} sx={{ textAlign: "center", py: 5 }}>
                    No Attendance Found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* VIEW EMPLOYEE DETAILS MODAL */}
      <Dialog open={viewOpen} onClose={() => setViewOpen(false)} maxWidth="xl" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", bgcolor: "#f8fafc" }}>
          <Box>
            <Typography sx={{ fontSize: "1rem", fontWeight: 700 }}>{viewEmployee?.name}</Typography>
            <Typography sx={{ fontSize: "0.7rem", color: "#666", fontFamily: "monospace" }}>
              {viewEmployee?.unique_id}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button size="small" variant="contained"
              startIcon={<Download fontSize="small" />}
              onClick={() => downloadEmployeeCSV(viewEmployee)}
              sx={{ fontSize: "0.7rem", textTransform: "none" }}>
              Download
            </Button>
            <IconButton size="small" onClick={() => setViewOpen(false)}><Close /></IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 2 }}>
          {viewEmployee && (
            <>
              <Box sx={{ mb: 2, p: 1.5, bgcolor: "#f0f9ff", borderRadius: 1, display: "flex", gap: 3, flexWrap: "wrap" }}>
                <Box><Typography sx={{ fontSize: "0.65rem", color: "#666" }}>MOBILE</Typography>
                  <Typography sx={{ fontSize: "0.8rem" }}>{viewEmployee.mobile || "-"}</Typography></Box>
                <Box><Typography sx={{ fontSize: "0.65rem", color: "#666" }}>EMAIL</Typography>
                  <Typography sx={{ fontSize: "0.8rem" }}>{viewEmployee.email || "-"}</Typography></Box>
                <Box><Typography sx={{ fontSize: "0.65rem", color: "#666" }}>ADDRESS</Typography>
                  <Typography sx={{ fontSize: "0.8rem" }}>{viewEmployee.address || "-"}</Typography></Box>
              </Box>

              <TableContainer component={Paper} sx={{ maxHeight: "60vh" }}>
                <Table stickyHeader size="small" sx={{
                  "& .MuiTableCell-root": { padding: "6px 12px !important", fontSize: "0.75rem" },
                  "& thead th": { fontWeight: "bold", bgcolor: "#f8fafc" },
                }}>
                  <TableHead>
                    <TableRow>
                      <TableCell align="center">#</TableCell>
                      <TableCell align="center">Date</TableCell>
                      <TableCell align="center">Status</TableCell>
                      <TableCell align="center">In Time</TableCell>
                      <TableCell align="center">Out Time</TableCell>
                      <TableCell align="center">Total</TableCell>
                      <TableCell align="center">Type</TableCell>
                      <TableCell align="center">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.keys(viewEmployee.attendance).sort().reverse().map((date, i) => {
                      const r = viewEmployee.attendance[date];
                      const isHoliday = holidayMap[date];
                      const isPresent = r.status === "Present";
                      return (
                        <TableRow key={date} hover sx={{ bgcolor: i % 2 === 0 ? "#fafafa" : "#fff" }}>
                          <TableCell align="center">{i + 1}</TableCell>
                          <TableCell align="center">{date}</TableCell>
                          <TableCell align="center">
                            {isHoliday ? (
                              <Chip label={`🎉 ${isHoliday}`} size="small"
                                sx={{ bgcolor: "#fef9c3", color: "#a16207", fontSize: "0.65rem", height: 20, fontWeight: 600 }} />
                            ) : (
                              <Chip label={r.status} size="small"
                                sx={{
                                  bgcolor: isPresent ? "#dcfce7" : "#fee2e2",
                                  color: isPresent ? "#16a34a" : "#dc2626",
                                  fontSize: "0.65rem", height: 20, fontWeight: 600
                                }} />
                            )}
                          </TableCell>
                          <TableCell align="center">{r.in_time || "-"}</TableCell>
                          <TableCell align="center">{r.out_time || "-"}</TableCell>
                          <TableCell align="center">{r.total_time || "-"}</TableCell>
                          <TableCell align="center">
                            <Chip label={r.auto_marked ? "Auto" : "Manual"} size="small"
                              sx={{
                                bgcolor: r.auto_marked ? "#e5e7eb" : "#dbeafe",
                                color: r.auto_marked ? "#4b5563" : "#1e40af",
                                fontSize: "0.6rem", height: 18, fontWeight: 600
                              }} />
                          </TableCell>
                          <TableCell align="center">
                            {!isHoliday && (
                              <IconButton size="small" onClick={() => openEditRecord(date, r)}
                                sx={{ color: "#0ea5e9" }}>
                                <Edit fontSize="small" />
                              </IconButton>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* EDIT RECORD DIALOG */}
      <Dialog open={!!editRecord} onClose={() => setEditRecord(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: "#f0f9ff", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography sx={{ fontSize: "1rem", fontWeight: 700 }}>
            ✏️ Edit Attendance - {editRecord?.date}
          </Typography>
          <IconButton size="small" onClick={() => setEditRecord(null)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select value={editStatus} label="Status" onChange={(e) => setEditStatus(e.target.value)}>
                  <MenuItem value="Present">Present</MenuItem>
                  <MenuItem value="Absent">Absent</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            {editStatus === "Present" && (
              <>
                <Grid item xs={6}>
                  <TextField fullWidth size="small" type="time" label="In Time"
                    value={editInTime} onChange={(e) => setEditInTime(e.target.value)}
                    InputLabelProps={{ shrink: true }} />
                </Grid>
                <Grid item xs={6}>
                  <TextField fullWidth size="small" type="time" label="Out Time"
                    value={editOutTime} onChange={(e) => setEditOutTime(e.target.value)}
                    InputLabelProps={{ shrink: true }} />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ bgcolor: "#f5f5f5" }}>
          <Button onClick={() => setEditRecord(null)}>Cancel</Button>
          <Button onClick={saveEditRecord} variant="contained" disabled={savingEdit}
            startIcon={savingEdit ? <CircularProgress size={16} /> : <Save />}>
            {savingEdit ? "Saving..." : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* HOLIDAY MANAGEMENT MODAL */}
      <Dialog open={holidayOpen} onClose={() => setHolidayOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", bgcolor: "#fef9c3" }}>
          <Typography sx={{ fontSize: "1rem", fontWeight: 700, color: "#a16207" }}>🎉 Manage Holidays</Typography>
          <IconButton size="small" onClick={() => setHolidayOpen(false)}><Close /></IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          <Box sx={{ mb: 3, p: 2, bgcolor: "#f8fafc", borderRadius: 1 }}>
            <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, mb: 1.5 }}>Add New Holiday</Typography>
            <Grid container spacing={1}>
              <Grid item xs={12} sm={5}>
                <TextField fullWidth size="small" type="date" label="Date"
                  value={holidayDate} onChange={(e) => setHolidayDate(e.target.value)}
                  InputLabelProps={{ shrink: true }} />
              </Grid>
              <Grid item xs={12} sm={5}>
                <TextField fullWidth size="small" label="Holiday Name"
                  value={holidayName} onChange={(e) => setHolidayName(e.target.value)}
                  placeholder="e.g., Diwali" />
              </Grid>
              <Grid item xs={12} sm={2}>
                <Button fullWidth variant="contained" color="warning"
                  onClick={addHoliday} startIcon={<Add />} sx={{ height: 40 }}>
                  Add
                </Button>
              </Grid>
            </Grid>
          </Box>

          <Typography sx={{ fontSize: "0.85rem", fontWeight: 600, mb: 1 }}>All Holidays ({holidays.length})</Typography>
          {holidays.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 4, color: "#999" }}>
              <Typography sx={{ fontSize: "2rem" }}>📅</Typography>
              <Typography sx={{ fontSize: "0.85rem" }}>No holidays yet</Typography>
            </Box>
          ) : (
            <List dense sx={{ maxHeight: 300, overflow: "auto", bgcolor: "#fff", border: "1px solid #eee", borderRadius: 1 }}>
              {holidays.map((h) => (
                <ListItem key={h.id} divider
                  secondaryAction={
                    <IconButton edge="end" onClick={() => deleteHoliday(h.id)} sx={{ color: "#dc2626" }}>
                      <Delete fontSize="small" />
                    </IconButton>
                  }>
                  <EventAvailable sx={{ color: "#a16207", mr: 1.5, fontSize: 20 }} />
                  <ListItemText
                    primary={<Typography sx={{ fontSize: "0.85rem", fontWeight: 600 }}>{h.name}</Typography>}
                    secondary={<Typography sx={{ fontSize: "0.7rem", color: "#666" }}>{h.date}</Typography>} />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
      </Dialog>

      {/* DELETE MONTHLY RECORDS DIALOG */}
      <Dialog open={deleteMonthOpen} onClose={() => setDeleteMonthOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ bgcolor: "#fee2e2", color: "#991b1b" }}>
          🗑️ Delete Monthly Records
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <Typography sx={{ fontSize: "0.85rem", mb: 2 }}>
            Select month to delete <strong>ALL attendance records</strong> for that month
          </Typography>
          <TextField
            fullWidth size="small" type="month" label="Select Month"
            value={deleteMonth} onChange={(e) => setDeleteMonth(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <Typography sx={{ fontSize: "0.7rem", color: "#dc2626", mt: 2 }}>
            ⚠️ This will delete all records for the selected month. Cannot be undone!
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteMonthOpen(false)}>Cancel</Button>
          <Button onClick={handleMonthlyDelete} variant="contained" color="error" startIcon={<DeleteSweep />}>
            Delete Month
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default EmployeeAttendance;